import { useState } from 'react';
import { formatDate } from '../../../lib/format';
import { Link, useParams } from 'react-router-dom';
import type { AuditRow, CatalogueHazardDetail, CursorPage } from '@dru/types';
import { INCLUSION_LABEL } from '@dru/types';
import { Alert, Button, Detail, DetailHeader, DetailSection, Dialog, InfoTip, KeyFigures, StatusBadge, TextField, TextSkeleton } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { CatalogueState, RetireAction, plural } from '../components/RetireAction';
import { ActivityTable, NotForYourRole } from './Audit';

import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';

/** One hazard: what it obliges, which industries carry it, which rules key on it. */
export function HazardDetail() {
  if (!useAuth().permissions.has('protocol.read')) {
    return <NotForYourRole title="Hazard" what="The clinical catalogue is maintained by platform administrators." />;
  }
  return <HazardDetailPage />;
}

function HazardDetailPage() {
  const { id = '' } = useParams();
  const hazard = useQuery<CatalogueHazardDetail>(() => api.get(`/hazards/${id}`), [id]);
  const trail = useQuery<CursorPage<AuditRow>>(() => api.get(`/audit?entityType=HazardCategory&entityId=${id}&limit=50`), [id]);
  const [editing, setEditing] = useState(false);
  const h = hazard.data;
  useBreadcrumbTitle(h?.name);
  const canManage = useAuth().permissions.has('hazard.manage');
  const liveProtocols = h?.protocols.filter((p) => p.isActive) ?? [];

  // Throws, so the confirmation stays open with the reason when refused.
  async function toggle() {
    if (!h) return;
    await api.patch(`/hazards/${id}`, { isActive: !h.isActive });
    hazard.reload();
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Hazard"
        title={h?.name ?? (hazard.error ? 'Hazard not found' : 'Loading…')}
        badges={h && <CatalogueState active={h.isActive} />}
        subtitle={h && [h.code, h.description].filter(Boolean).join(' · ')}
        actions={h && canManage && (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
            <RetireAction
              variant="button"
              active={h.isActive}
              name={h.name}
              what="hazard"
              onConfirm={toggle}
              impact={h.isActive
                ? `Mapped to ${plural(h.jobRoleCount, 'job role')} across companies; ${plural(liveProtocols.length, 'live protocol')} key on it.`
                : undefined}
              consequence={h.jobRoleCount && liveProtocols.length
                ? `Employees in those ${plural(h.jobRoleCount, 'job role')} stop being owed ${liveProtocols.length === 1 ? 'the protocol' : 'the protocols'} this hazard brings, from their next examination. Companies can no longer map it to a job role.`
                : 'Companies can no longer map it to a job role, and it stops seeding the hazard picker at registration.'}
            />
          </>
        )}
      />
      {hazard.error && <Alert tone="danger">{hazard.error}</Alert>}
      {hazard.loading && !h && <TextSkeleton lines={6} />}
      {h && (
        <KeyFigures
          figures={[
            { key: 'protocols', label: 'Live protocols', value: liveProtocols.length, tone: liveProtocols.length ? undefined : 'overdue', hint: liveProtocols.length ? 'owed to an exposed employee' : 'owes an employee nothing' },
            { key: 'roles', label: 'Job roles', value: h.jobRoleCount, hint: 'across companies' },
            { key: 'industries', label: 'Industries', value: h.industries.length, hint: 'list it as typical' },
            { key: 'rules', label: 'Package rules', value: h.rules.length },
          ]}
        />
      )}
      {h && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          <DetailSection title={`Protocols it brings (${h.protocols.length})`}>
            {h.protocols.length === 0 ? (
              <Alert tone="warning">No protocol is linked, so an employee exposed to this hazard is owed no examination for it. Link one by publishing a protocol version that lists it.</Alert>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {[...h.protocols].sort((a, b) => Number(b.isActive) - Number(a.isActive)).map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-small">
                    <Link to={`/admin/masters/protocols/${p.id}`} className="inline-block min-w-0 py-1 text-primary-700 hover:underline">{p.name} <span className="font-mono text-caption text-neutral-500">{p.code} v{p.version}</span></Link>
                    <span className="flex items-center gap-2 text-caption text-neutral-500">
                      {plural(p.testCount, 'test')} · every {p.periodicityMonths} mo
                      {!p.isActive && <StatusBadge>Superseded</StatusBadge>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          <DetailSection
            title="Where it is found"
            action={(
              <InfoTip label="About where it is found">
                Job roles belong to companies, so only their number shows here.
              </InfoTip>
            )}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Detail label="Added" value={formatDate(h.createdAt)} />
              <Detail label="Last changed" value={formatDate(h.updatedAt)} />
            </div>
            {h.industries.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {h.industries.map((i) => (
                  <li key={i.id}><Link to={`/admin/masters/industries/${i.id}`} className="inline-flex min-h-[28px] max-md:min-h-10 items-center"><StatusBadge tone="neutral">{i.name}</StatusBadge></Link></li>
                ))}
              </ul>
            )}
            {h.industries.length === 0 && <p className="mt-3 text-small text-neutral-500">No industry lists it as typical.</p>}
          </DetailSection>

          <DetailSection phoneFolded title={`Package rules (${h.rules.length})`} className="lg:col-span-2">
            {h.rules.length === 0 ? <p className="text-small text-neutral-500">No rule suggests a package for this exposure. Add one from an industry page.</p> : (
              <ul className="divide-y divide-neutral-100">
                {h.rules.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 py-1.5 text-small">
                    <span className="text-neutral-800">{r.name} → <Link to={`/admin/masters/packages/${r.package.id}`} className="inline-block py-1 text-primary-700 hover:underline">{r.package.name}</Link></span>
                    <StatusBadge tone={r.tier === 'MANDATORY' ? 'covered' : r.tier === 'RECOMMENDED' ? 'pending' : 'neutral'}>{INCLUSION_LABEL[r.tier]}</StatusBadge>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          <DetailSection title="History" className="lg:col-span-2">
            <ActivityTable rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload}
              emptyHint="Changes to this hazard appear here." />
          </DetailSection>
        </div>
      )}
      {h && editing && <EditHazard hazard={h} onClose={() => setEditing(false)} onDone={() => { setEditing(false); hazard.reload(); }} />}
    </div>
  );
}

function EditHazard({ hazard, onClose, onDone }: { hazard: CatalogueHazardDetail; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(hazard.name);
  const [description, setDescription] = useState(hazard.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true); setError(null);
    try { await api.patch(`/hazards/${hazard.id}`, { name: name.trim(), description: description.trim() }); onDone(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  return (
    <Dialog
      title={`Edit ${hazard.name}`}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={name.trim().length < 2}>Save</Button>
        </>
      )}
    >
      <div className="mt-3 space-y-4">
        <p className="text-small text-neutral-600">
          The new wording shows wherever this hazard is listed, including in {plural(hazard.jobRoleCount, 'company job role')}.
          What an employee is owed does not change.
        </p>
        <TextField label="Name" required value={name} onChange={setName} autoFocus />
        <TextField label="Where it occurs (optional)" value={description} onChange={setDescription} />
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}
